import { FC, useEffect } from 'react';
import { useIntl } from 'react-intl';
import {
  Navigate,
  Route,
  RouteProps,
  Routes,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useConfigContext } from '@fe-core';
import {
  BaseErrorEvent,
  BaseInfoEvent,
  publishBaseErrorEvent,
  publishBaseInfoEvent,
  useCheckServiceAvailability,
} from '@fe-core-events';
import { Tabs } from '@fe-learner-common/components/Tabs';
import { TSelfregSessionDetails } from '@api/io/selfreg.io';
import { NextGenUiToggle } from '@components/NextGenUiToggle';
import { OverviewTab } from '@components/tabs/overview/OverviewTab';
import { InstructorsTab } from '@components/tabs/InstructorsTab';
import { ParticipantsTab } from '@components/tabs/ParticipantsTab';
import { EventsTab } from '@components/tabs/events/EventsTab';
import { useParam } from '@hooks/params/useParam';
import { SelfRegPage } from '@routes/selfreg/SelfRegPage';
import { tabMap } from '@utils/tabMap';

export type TTabKeys = 'overview' | 'participants' | 'instructors' | 'events';
type TTabRoute = RouteProps & {
  enabled?: boolean;
};

type CourseSelfRegDetailsProps = {
  sessionDetails: TSelfregSessionDetails;
};

type CourseSelfRegAutoEnrollError = {
  title: string;
  description: string;
  status: 'info' | 'error';
};

// this is backend declared but not exhaustive list of possible errors,
// some codes matches cases where whole page fails during loading phase
const AUTOENROLL_ERRORCODES: Record<string, CourseSelfRegAutoEnrollError> = {
  ERROR_COURSE_FULL: {
    title: 'catalog.course.error.autoenroll.limit.title',
    description: 'catalog.course.error.autoenroll.limit.description',
    status: 'error',
  },
  ERROR_CREDITRESERVATION: {
    title: 'catalog.course.error.autoenroll.credits.title',
    description: 'catalog.course.error.autoenroll.credits.description',
    status: 'error',
  },
  ERROR_PARTICIPATION_IN_WAITILIST: {
    title: 'catalog.course.error.autoenroll.waitlist.title',
    description: 'catalog.course.error.autoenroll.waitlist.description',
    status: 'info',
  },
  ERROR_PARTICIPATION_NOT_APPROVED: {
    title: 'catalog.course.error.autoenroll.approve.title',
    description: 'catalog.course.error.autoenroll.approve.description',
    status: 'info',
  },
};

const useAutoEnrollErrorHandler = () => {
  const intl = useIntl();
  const { value, removeParam } = useParam({ key: 'error' });

  const enabled = useCheckServiceAvailability({
    serviceId: 'core.feedback.ui',
  });

  useEffect(() => {
    if (!value || !enabled) {
      return;
    }

    const { status, title, description } = AUTOENROLL_ERRORCODES[value] ?? {
      title: 'catalog.course.error.autoenroll.unknown.title',
      description: 'catalog.course.error.autoenroll.unknown.description',
      status: 'error',
    };

    const error = {
      status,
      type: 'modal',
      consumer: 'fe-learner-catalog',
      event: `feedback.ui.${status}.base`,
      config: {
        title: intl.formatMessage({ id: title }),
        description: intl.formatMessage({ id: description }),
      },
    };

    switch (error.status) {
      case 'info':
        publishBaseInfoEvent(error as BaseInfoEvent);
        break;
      case 'error':
        publishBaseErrorEvent(error as BaseErrorEvent);
        break;
    }

    removeParam();
  }, [value, removeParam, enabled, intl]);

  return null;
};

export const CourseSelfRegDetails: FC<CourseSelfRegDetailsProps> = ({
  sessionDetails,
}) => {
  const selectedTab = useParams()['*'];
  const [searchParams] = useSearchParams();
  const { extension } = useConfigContext();
  const hiddenTabs = extension?.['fe-learner-catalog']?.hiddenTabs ?? [];

  const tabRoutes: Record<TTabKeys, TTabRoute> = {
    overview: {
      enabled: true,
      path: 'overview',
      element: <OverviewTab sessionDetails={sessionDetails} />,
    },
    instructors: {
      enabled: true,
      path: 'instructors',
      element: <InstructorsTab persons={sessionDetails.trainers} />,
    },
    participants: {
      enabled:
        sessionDetails.participantInfo?.enabled &&
        sessionDetails.participantInfo?.showThumbnail,
      path: 'participants',
      element: <ParticipantsTab persons={sessionDetails.participants} />,
    },
    events: {
      enabled: true,
      path: 'events',
      element: <EventsTab dates={sessionDetails.dates} />,
    },
  };

  const availableTabs = Object.entries(tabRoutes).filter(
    ([tab, route]) => route.enabled && !hiddenTabs.includes(tab),
  );
  const availableTabKeys = availableTabs.map(([tab]) => tab as TTabKeys);
  const [firstAvailableTabKey] = availableTabKeys;
  const tabs = availableTabKeys.map((key) => tabMap[key]);

  useAutoEnrollErrorHandler();

  return (
    <Routes>
      <Route
        element={
          <>
            <NextGenUiToggle isLoading={false} />
            <SelfRegPage sessionDetails={sessionDetails}>
              <Tabs tabs={tabs} selectedTab={selectedTab} />
            </SelfRegPage>
          </>
        }
      >
        {availableTabs.map(([tabKey, route]) => (
          <Route key={tabKey} {...route} />
        ))}
      </Route>
      <Route
        path="*"
        element={
          <Navigate
            to={
              searchParams.size > 0
                ? `${firstAvailableTabKey}?${searchParams.toString()}`
                : firstAvailableTabKey
            }
            replace
          />
        }
      />
    </Routes>
  );
};
